import { createContext, useCallback, useContext, useEffect, useRef } from "react";

import stableStringify from "json-stable-stringify";
import { FormProvider, type UseFormReturn } from "react-hook-form";

import useNavigation from "../../../hooks/navigation";
import {
  type CreateChargebeeCheckoutResponseData,
  EmptyCheckoutEstimateFormData,
  type EstimateResponseData,
  formDataToEstimateFormData,
  useEstimate,
} from "../../../hooks/useCheckout";
import usePrevious from "../../../hooks/usePrevious";
import { SearchParam } from "../../../types";
import { GenericErrorModal } from "../../Modal/Modal";
import type { CheckoutFormData } from "../ChargebeeCheckoutSchema";

const CheckoutFormContext = createContext<{
  methods: UseFormReturn<CheckoutFormData, unknown, CheckoutFormData>;
  estimateMutationResult?: ReturnType<typeof useEstimate>;
  couponCode?: string;
  submitError: string | null;
  checkout: CreateChargebeeCheckoutResponseData | undefined;
  isMigrationConsentRequired?: boolean;
}>({
  methods: {} as UseFormReturn<CheckoutFormData, unknown, CheckoutFormData>,
  estimateMutationResult: undefined,
  submitError: null,
  checkout: undefined,
});

export const useCheckoutFormContext = () => {
  return useContext(CheckoutFormContext);
};

export default function CheckoutFormProvider({
  methods,
  children,
  submitError,
  checkout,
  submit,
  isMigrationConsentRequired,
}: {
  methods: UseFormReturn<CheckoutFormData, unknown, CheckoutFormData>;
  children: React.ReactNode;
  submitError: string | null;
  checkout: CreateChargebeeCheckoutResponseData | undefined;
  submit: (
    estimate: EstimateResponseData | undefined,
    checkoutID: string | undefined,
    formData: CheckoutFormData
  ) => Promise<void>;
  isMigrationConsentRequired?: boolean;
}) {
  const { watch, clearErrors, handleSubmit, setError, getValues } = methods;

  const estimateMutationResult = useEstimate({
    checkoutID: checkout?.id,
    setError,
    clearErrors,
  });
  const {
    mutate: estimateMutation,
    data: estimateResponseData,
    isError: isEstimateError,
  } = estimateMutationResult;

  const previousCheckoutID = usePrevious(checkout?.id);
  useEffect(() => {
    if (checkout?.id && checkout?.id != previousCheckoutID) {
      const estimateFormData = formDataToEstimateFormData(getValues());
      estimateMutation({ estimateFormData });
      estimateMutation.flush();
    }
  }, [checkout?.id, estimateMutation, getValues, previousCheckoutID]);

  const previousEstimateFormData = useRef(EmptyCheckoutEstimateFormData);
  useEffect(() => {
    const subscription = watch(async (data, { name }) => {
      if (name === "migrationConsent") {
        return;
      }
      if (checkout?.id) {
        const estimateFormData = formDataToEstimateFormData(data as CheckoutFormData);
        if (
          stableStringify(estimateFormData) != stableStringify(previousEstimateFormData.current) ||
          data.couponCode != previousEstimateFormData.current.couponCode
        ) {
          estimateFormData.couponCode = data.couponCode;

          estimateMutation({ estimateFormData });
          if (
            estimateFormData.period != previousEstimateFormData.current?.period ||
            estimateFormData.countryCode != previousEstimateFormData.current?.countryCode ||
            estimateFormData.stateCode != previousEstimateFormData.current?.stateCode ||
            estimateFormData.couponCode != previousEstimateFormData.current?.couponCode
          ) {
            estimateMutation.flush();
          }
          previousEstimateFormData.current = { ...estimateFormData };
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors, checkout?.id, estimateMutation]);

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name === "countryCode") {
        clearErrors();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const navigate = useNavigation();
  const onCheckoutClose = useCallback(
    () =>
      navigate.goToProfile([
        SearchParam.SubscriptionPeriod,
        SearchParam.SubscriptionTier,
        SearchParam.FromAddSeats,
      ]),
    [navigate]
  );

  return (
    <CheckoutFormContext.Provider
      value={{
        methods,
        estimateMutationResult,
        submitError,
        checkout,
        isMigrationConsentRequired,
      }}
    >
      <FormProvider {...methods}>
        <form
          style={{ width: "100%" }}
          onSubmit={handleSubmit(submit.bind(null, estimateResponseData, checkout?.id))}
          noValidate
        >
          {children}
        </form>
      </FormProvider>
      <GenericErrorModal open={isEstimateError} onClose={onCheckoutClose} />
    </CheckoutFormContext.Provider>
  );
}
